import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Shield, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back.");
      const dest = user.role === "employee" ? "/dashboard" : user.role === "super_admin" ? "/admin/content" : "/admin";
      navigate(dest);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign in failed.");
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={AUTH_IMG} alt="A supportive conversation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest-900/60" />
        <div className="relative h-full flex flex-col justify-between p-12 text-bone">
          <Link to="/" className="flex items-center gap-2.5 w-fit">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-terracotta-400 text-white"><Shield className="h-5 w-5" strokeWidth={1.5} /></span>
            <span className="font-serif text-lg font-semibold">Patria ER CoE</span>
          </Link>
          <div>
            <h2 className="font-serif text-4xl leading-tight">Consistent, impartial care for every campus.</h2>
            <p className="mt-4 text-bone/80 max-w-md">Sign in to manage concerns, coordinate reviews, and track every matter to resolution.</p>
          </div>
          <span className="text-xs text-bone/50">Confidential by design.</span>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-forest-800 mb-8 lg:hidden">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Home
          </Link>
          <h1 className="font-serif text-3xl text-forest-900">Sign in</h1>
          <p className="mt-2 text-charcoal-600">Access your ER CoE workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-forest-900 font-semibold">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-forest-900 font-semibold">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full rounded-full bg-forest-600 hover:bg-forest-700 text-bone py-6 text-base">
              {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-charcoal-600">
            New employee? <Link to="/register" className="font-semibold text-terracotta-500 hover:underline">Create an account</Link>
          </p>

          <div className="mt-8 rounded-xl bg-bone-100/70 border border-forest-600/10 p-4 text-xs text-charcoal-600" data-testid="demo-accounts">
            <p className="font-semibold text-forest-800 mb-2">Demo accounts (click to fill)</p>
            <div className="space-y-1.5">
              <button onClick={() => quick("specialist@patria.church", "Specialist@2026")} className="block hover:text-forest-800">Specialist — specialist@patria.church</button>
              <button onClick={() => quick("employee@patria.church", "Employee@2026")} className="block hover:text-forest-800">Employee — employee@patria.church</button>
              <button onClick={() => quick("admin@patria.church", "Admin@Patria2026")} className="block hover:text-forest-800">Admin — admin@patria.church</button>
              <button onClick={() => quick("superadmin@patria.church", "Super@Patria2026")} className="block hover:text-forest-800">Super Admin — superadmin@patria.church</button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
